import { useTranslation } from 'react-i18next';
import {
  Heart,
  Trophy,
  Flame,
  TrendingUp,
  Award,
  AlertTriangle,
  UserPlus,
  Users,
  Target,
} from 'lucide-react';
import type { NotificationKind } from '@ember/shared';
import type { NotificationItem as NotificationData } from '@/lib/api';
import { Card, Button } from '@/components/ui';
import { formatRelativeTime } from '@/lib/format';
import { useAcceptFriendRequest, useDeclineFriendRequest } from '@/hooks/useFriends';
import { useMarkNotificationRead } from '@/hooks/useNotifications';
import { cn } from '@/lib/cn';
import { toastApiError } from '@/lib/toast';

const KIND_ICON: Record<NotificationKind, typeof Heart> = {
  friend_request: UserPlus,
  friend_accepted: Heart,
  achievement: Award,
  level_up: TrendingUp,
  overtaken: Trophy,
  streak_in_danger: AlertTriangle,
  challenge_reminder: Target,
  challenge_completed: Target,
  community_invite: Users,
};

interface Props {
  notification: NotificationData;
}

export function NotificationItem({ notification }: Props) {
  const { t } = useTranslation();
  const Icon = KIND_ICON[notification.kind] ?? Flame;
  const accept = useAcceptFriendRequest();
  const decline = useDeclineFriendRequest();
  const markRead = useMarkNotificationRead();

  const payload = notification.payload ?? {};
  const message = renderMessage(notification.kind, payload, t);
  const isUnread = !notification.readAt;

  const onClick = () => {
    if (isUnread) markRead.mutate(notification.id);
  };

  const fromUserId =
    typeof payload['userId'] === 'string' ? (payload['userId'] as string) : null;

  return (
    <Card
      className={cn(
        'flex items-start gap-3 transition-colors',
        isUnread && 'bg-accent/5 border-accent/20',
      )}
      onClick={onClick}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-parchment-ink">{message}</p>
        <p className="mt-0.5 text-xs text-parchment-muted">
          {formatRelativeTime(notification.createdAt)}
        </p>
        {notification.kind === 'friend_request' && fromUserId && (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              isLoading={accept.isPending}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await accept.mutateAsync(fromUserId);
                } catch (err) {
                  toastApiError(err);
                }
              }}
            >
              {t('friends.accept')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              isLoading={decline.isPending}
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  await decline.mutateAsync(fromUserId);
                } catch (err) {
                  toastApiError(err);
                }
              }}
            >
              {t('friends.decline')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

type TFn = ReturnType<typeof useTranslation>['t'];

function renderMessage(
  kind: NotificationKind,
  payload: Record<string, unknown>,
  t: TFn,
): string {
  const get = (k: string): string => (typeof payload[k] === 'string' ? (payload[k] as string) : '');
  switch (kind) {
    case 'friend_request':
      return t('notifications.kinds.friend_request', { from: get('username') });
    case 'friend_accepted':
      return t('notifications.kinds.friend_accepted', { from: get('username') });
    case 'achievement':
      return t('notifications.kinds.achievement', { name: get('name') });
    case 'level_up':
      return t('notifications.kinds.level_up', { level: payload['level'] ?? '' });
    case 'overtaken':
      return t('notifications.kinds.overtaken', { by: get('username') });
    case 'streak_in_danger':
      return t('notifications.kinds.streak_in_danger');
    case 'challenge_reminder':
      return t('notifications.kinds.challenge_reminder', { title: get('title') });
    case 'challenge_completed':
      return t('notifications.kinds.challenge_completed', { title: get('title') });
    case 'community_invite':
      return t('notifications.kinds.community_invite', {
        from: get('from'),
        community: get('community'),
      });
    default:
      return '';
  }
}
